/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { PatientInterface, UserRolesEnum } from "medicalsystem-shared";
import { getNewPatientDataContent } from "utils/model.utils";
import NewStepperForm from "../StepperForm";
import { CoreAPI_URLs } from "constants/http.const";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import useHttp from "hooks/http.hook";
import { ExtendedPersonInterface } from "interfaces";
import { RoutesConstants } from "medicalsystem-shared";

/**
 * Dynamic stepper form wrapper for creating new patients.
 * Initializes a new patient model and configures the stepper form with post-processing
 * for parent/guardian relationship creation.
 * 
 * Features:
 * - Creates new patient data model with defaults
 * - Multi-step patient registration workflow
 * - Post-processing handler for parent/guardian relationships
 * - Automatic relationship creation after patient save
 * - Date management for relationship grant/expiration dates
 * - Navigation to patients list after completion
 * - Integration with person relations API
 * 
 * Post-Processing Flow:
 * 1. Patient is created via stepper form
 * 2. Parent/guardian relationships are extracted from form data
 * 3. For each relationship:
 *    - Creates PersonRelation linking patient to parent/guardian
 *    - Sets relationship type (mother, father, guardian, etc.)
 *    - Marks main/responsible guardian
 *    - Adds custom relationship text and comments
 * 4. Redirects to patients list after 1 second delay
 * 
 * Relationship Fields:
 * - grantor: The patient (child)
 * - grantee: The parent/guardian
 * - relationship: Type of relationship
 * - customRelationship: Custom relationship text
 * - mainGrantee: Flag for primary responsible party
 * - comments: Additional notes
 * 
 * Use Cases:
 * - New patient registration with parent/guardian info
 * - Minor patient account creation requiring legal guardians
 * - Multi-step patient onboarding process
 * 
 * @returns {React.ReactElement} The configured stepper form for patient creation
 */
const NewDynamicPatientStepperForm = () => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [currentDate, setCurrentDate] = useState("");
  const navigate = useNavigate();
  const patient: PatientInterface = getNewPatientDataContent();

  useEffect(() => {
    const date = new Date();
    const formattedDate = date.toISOString().split("T")[0];
    setCurrentDate(formattedDate);
  }, []);

  const PatientStepperForm = NewStepperForm<PatientInterface>;
  const postProcessingHandler = (
    model: Partial<PatientInterface>,
    payload: ExtendedPersonInterface[]
  ) => {
    if (model.person) {
      payload.forEach((item, index) => {
        const relationModel = {
          grantor: { id: model.person.id },
          grantee: { id: item.id },
          relationship: item.relationship,
          grantDate: currentDate,
          expirationDate: currentDate,
          customRelationship: item.other,
          comments: item.comment,
          mainGrantee: item.responsible,
        };
        sendRequest(
          CoreAPI_URLs.PERSONS_RELATION_GENERAL_OPERATIONS,
          null,
          "POST",
          JSON.stringify(relationModel),
          null,
          null
        );
      });
    } else {
      console.error("model.person is undefined");
    }
    setTimeout(() => {
      navigate(RoutesConstants.PATIENTS);
    }, 1000);
  };

  return (
    <PatientStepperForm
      model={patient}
      redirect={RoutesConstants.PATIENTS}
      type={UserRolesEnum.PATIENT}
      postProcessingHandler={postProcessingHandler}
    />
  );
};

export default NewDynamicPatientStepperForm;
